import {Injectable, Inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {DraftJobChanges} from '../../models/draft-job-changes.model';
import {DraftJobChangesFilter} from '../../models/draft-job-changes-filter.model';
import {BatchModel} from '../../../batch/models/batch.model';
import {ProcessTrackingModel} from '../../../commons/models/process-tracking.model';
import {ProcessTrackingTypeEnum} from '../../../commons/types/process-tracking-type.enum';
import {ProcessTrackingStatusEnum} from '../../../commons/types/process-tracking-status.enum';

// Matches Spring Data's default Page<DraftJobChanges> JSON (DraftJobChangesController.jobChanges
// in bringoz-server returns org.springframework.data.domain.Page verbatim, no custom serializer) —
// trimmed to the fields this app reads; the wire payload also carries pageable/sort/first/etc.
export interface DraftJobChangesPage {
  content: DraftJobChanges[];
  totalElements: number;
  totalPages?: number;
  number?: number;
  size?: number;
}

export interface DraftJobChangesCursorList {
  cursor: string;
  list: DraftJobChanges[];
}

// `query` is a forward-looking param only — the jobChanges/page endpoint does not support
// free-text search yet (confirmed against DraftJobChangesController/DraftJobChangesFilter in
// bringoz-server, which only take the exact-match filter fields below). Sent already so the UI
// is ready the moment the backend adds support; until then the server should just ignore it.
export interface DraftJobChangesPageParams extends DraftJobChangesFilter {
  page: number;
  size: number;
  sort?: string;
  query?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PlanningDraftJobChangesHttpService {
  // The spec's "v2" refers to the api-v2 App Engine service, not a literal URL prefix —
  // these controllers are registered under plain "/planning", same as every other
  // planning HTTP service in this app (confirmed against PlanningV2Controller /
  // DraftJobChangesController's @RequestMapping in bringoz-server).
  private readonly base_url = '/planning/projects';
  private readonly process_tracking_url = '/planning/processTracking/process';

  constructor(@Inject(HttpClient) private http: HttpClient) {
  }

  scan(projectId: string, draftId: string): Observable<BatchModel> {
    return this.http.post<BatchModel>(`${this.base_url}/${projectId}/drafts/${draftId}/jobChanges/scan`, {});
  }

  getPage(projectId: string, draftId: string, params: DraftJobChangesPageParams): Observable<DraftJobChangesPage> {
    return this.http.get<DraftJobChangesPage>(`${this.base_url}/${projectId}/drafts/${draftId}/jobChanges/page`, {params: params as any});
  }

  getCursor(projectId: string, draftId: string, params: DraftJobChangesFilter & { cursor?: string, limit?: number, sort?: string }): Observable<DraftJobChangesCursorList> {
    return this.http.get<DraftJobChangesCursorList>(`${this.base_url}/${projectId}/drafts/${draftId}/jobChanges`, {params: params as any});
  }

  dismiss(projectId: string, draftId: string, filter: DraftJobChangesFilter): Observable<void> {
    return this.http.delete<void>(`${this.base_url}/${projectId}/drafts/${draftId}/jobChanges`, {params: filter as any});
  }

  apply(projectId: string, draftId: string, filter: DraftJobChangesFilter): Observable<BatchModel> {
    return this.http.post<BatchModel>(`${this.base_url}/${projectId}/drafts/${draftId}/jobChanges/apply`, {}, {params: filter as any});
  }

  getProcessTracking(projectId: string, draftId: string, params?: { type?: ProcessTrackingTypeEnum, status?: ProcessTrackingStatusEnum }): Observable<ProcessTrackingModel[]> {
    return this.http.get<ProcessTrackingModel[]>(`${this.process_tracking_url}/${projectId}/${draftId}`, {params: params as any});
  }
}
